import * as Phaser from 'phaser';
import { gameClient } from '../network/GameClient';
import { SHIP_DEFINITIONS, SECONDARY_WEAPONS, ServerEvents, ARENAS, generateObstacles, TIER_COLORS, TIER_MULTIPLIERS, BUFF_DURATION, RANK_NAMES, SECONDARY_AMMO_BONUS, getRankMult, RESPAWN_DELAY_SECONDS } from '@rttv/shared';
import type { PlayerState, PlayerDiedPayload, PlayerKilledPayload, ShipId, PowerupCollectedPayload, PowerupTier } from '@rttv/shared';
import { uiState } from '../uiState';
import { getHudWidth } from '../touch/TouchControls';
import { getShipVisual } from '../shipVisuals';

// ── Layout constants ─────────────────────────────────────────────────────────
// True constants — never change.
const HUD_PAD     = 16;
const BAR_H       = 28;
const ROW_H       = 68;
const MAP_LABEL_H = 28;
const WORLD_SIZE  = 3200;
const MAX_DOTS    = 16;

// Layout vars — recomputed at UIScene.create() time via recomputeHudLayout().
// Defaults match desktop (HUD_W = 420).
let HUD_W         = 420;
let BAR_W         = HUD_W - HUD_PAD * 2;   // 388 px
let MAP_SIZE      = BAR_W;
let MAP_SECTION_H = HUD_PAD + MAP_LABEL_H + MAP_SIZE + HUD_PAD;

// Column x offsets relative to bx (hudX + HUD_PAD) — also recomputed.
let COL_SHIP      = 190;
let COL_KILLS     = 306;
let COL_DEATHS    = BAR_W;

function recomputeHudLayout(hudW: number, compact: boolean) {
  HUD_W         = hudW;
  BAR_W         = HUD_W - HUD_PAD * 2;
  MAP_SIZE      = BAR_W;
  MAP_SECTION_H = HUD_PAD + MAP_LABEL_H + MAP_SIZE + HUD_PAD;
  COL_DEATHS    = BAR_W;
  if (compact) {
    COL_SHIP  = -9999;                          // hidden in compact mode
    COL_KILLS = Math.round(BAR_W * 0.72);
  } else {
    COL_SHIP  = 190;
    COL_KILLS = 306;
  }
}

// Toggle row — enemy overlay on/off button, between minimap and stats
const TOGGLE_H    = 36;
const STATS_H     = HUD_PAD + ROW_H * 3 - 14 + HUD_PAD;
const AMMO_H      = 38;
const PILOTS_LABEL_H = 28;
const PILOTS_ROW_H   = 17;

// These are computed from MAP_SECTION_H (a let), so they must be functions or
// recomputed via recomputeHudLayout. We make them pure functions of MAP_SECTION_H.
const statsY    = () => MAP_SECTION_H + TOGGLE_H;
const rowLabelY = (i: number) => statsY() + HUD_PAD + i * ROW_H;
const rowBarY   = (i: number) => statsY() + HUD_PAD + i * ROW_H + 24;
const ammoY     = () => statsY() + STATS_H;
const pilotsY   = () => ammoY() + AMMO_H;

const DIV_COLOR  = 0x6f4a24;
const DIV_ALPHA  = 0.85;
const ACCENT_CLR = 0xe08a2e;

const COL_RANK = 18;   // right-align — fixed, doesn't change with HUD_W
const COL_NAME = 24;   // left-align  — fixed

const MAX_SCORE_ROWS = 16;

// ── Round-end overlay constants ───────────────────────────────────────────────
const OV_CORNER    = 20;
const OV_W         = 660;
const OV_ROW_H     = 22;
const OV_MAX_ROWS  = 12;
const OV_BONUS_H   = 80;
const OV_FOOTER_H  = 40;
const OV_HEADER_H  = 80;
const OV_TABLE_H   = OV_ROW_H * 2 + OV_MAX_ROWS * OV_ROW_H;
const OV_H         = OV_HEADER_H + OV_TABLE_H + 16 + OV_BONUS_H + OV_FOOTER_H;

const MEDAL_COLORS = ['#ffd700', '#c0c0c0', '#cd7f32'];

// Overlay table column x offsets relative to panel left edge (px)
const OV_COL_RANK  = 44;
const OV_COL_NAME  = 52;
const OV_COL_K     = 310;
const OV_COL_D     = 400;
const OV_COL_DMG   = 510;
const OV_COL_STR   = 638;

// Bonus section x offsets
const OV_BONUS_LABEL_X = 20;
const OV_BONUS_NAME_X  = 230;

// ── Helpers ──────────────────────────────────────────────────────────────────

function addRect(
  scene: Phaser.Scene,
  x: number, y: number, w: number, h: number,
  color: number, alpha = 1, depth = 100,
): Phaser.GameObjects.Rectangle {
  return scene.add.rectangle(x, y, w, h, color, alpha)
    .setOrigin(0, 0)
    .setDepth(depth) as Phaser.GameObjects.Rectangle;
}

function addLine(
  scene: Phaser.Scene,
  x: number, y: number, w: number, h: number,
  color: number, alpha = 1, depth = 100,
): Phaser.GameObjects.Rectangle {
  return scene.add.rectangle(x, y, w, h, color, alpha)
    .setOrigin(0, 0)
    .setDepth(depth) as Phaser.GameObjects.Rectangle;
}

function tierHex(tier: string): string {
  const n = TIER_COLORS[tier as PowerupTier] ?? 0xffffff;
  return '#' + n.toString(16).padStart(6, '0');
}

function formatTime(seconds: number): string {
  const s = Math.max(0, seconds);
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60);
  return `${m}:${String(r).padStart(2, '0')}`;
}

// ── Scene ─────────────────────────────────────────────────────────────────────

export class UIScene extends Phaser.Scene {
  // Scoreboard — one Text object per cell, per row
  private scoreRanks:  Phaser.GameObjects.Text[] = [];
  private scoreNames:  Phaser.GameObjects.Text[] = [];
  private scoreShips:  Phaser.GameObjects.Text[] = [];
  private scoreKills:  Phaser.GameObjects.Text[] = [];
  private scoreDeaths: Phaser.GameObjects.Text[] = [];

  // Round timer box (top-left)
  private timerRoundLabel!: Phaser.GameObjects.Text;
  private roundTimerText!:  Phaser.GameObjects.Text;
  private arenaNameText!:   Phaser.GameObjects.Text;

  // Minimap — pre-allocated icon pool (one sprite per player slot)
  private mapIcons: Phaser.GameObjects.Image[] = [];
  private mapPips: Phaser.GameObjects.Rectangle[] = [];
  private mapX = 0;
  private mapY = 0;
  private obstaclesDrawn = false;

  // Minimap station markers (one per station)
  private stationDots: Phaser.GameObjects.Rectangle[] = [];
  // Minimap pad markers (two per station)
  private padDots: Phaser.GameObjects.Rectangle[] = [];
  // Minimap powerup dots — one per active powerup, colored by tier
  private powerupDots: Phaser.GameObjects.Rectangle[] = [];

  // Minimap explosion flashes
  private explosionPool:       Phaser.GameObjects.Rectangle[] = [];
  private explosionSubscribed  = false;

  // Stats bars
  private energyFill!:      Phaser.GameObjects.Rectangle;
  private energyValueText!: Phaser.GameObjects.Text;
  private energyLabel!:     Phaser.GameObjects.Text;
  private energyRateText!:  Phaser.GameObjects.Text;
  private shieldRateText!:  Phaser.GameObjects.Text;
  private armorRateText!:   Phaser.GameObjects.Text;

  // Buff bonus texts — shown in tier colour when a buff is active
  private energyBuffText!:  Phaser.GameObjects.Text;
  private shieldBuffText!:  Phaser.GameObjects.Text;
  private starBuffText!:    Phaser.GameObjects.Text;

  // Client-side active buff state (set on POWERUP_COLLECTED for the local player)
  private activeBoltBuff:   { tier: string; expiresAt: number } | null = null;
  private activeShieldBuff: { tier: string; expiresAt: number } | null = null;
  private activeStarBuff:   { tier: string; expiresAt: number } | null = null;
  private powerupSubscribed = false;

  private shieldFill!:      Phaser.GameObjects.Rectangle;
  private shieldValueText!: Phaser.GameObjects.Text;
  private shieldLabel!:     Phaser.GameObjects.Text;

  private armorFill!:       Phaser.GameObjects.Rectangle;
  private armorValueText!:  Phaser.GameObjects.Text;
  private armorLabel!:      Phaser.GameObjects.Text;

  // Ammo
  private ammoLabel!: Phaser.GameObjects.Text;
  private ammoText!:  Phaser.GameObjects.Text;
  private ammoPips!:  Phaser.GameObjects.Text;

  private hudX = 0;

  // Computed from HUD_W in create() — used at update() time
  private barW       = 388;
  private mapScale   = MAP_SIZE / WORLD_SIZE;
  private compactHud = false;

  // Rates — sampled every 500 ms for a stable, readable display
  private rateEnergySample = -1;
  private rateShieldSample = -1;
  private rateArmorSample  = -1;
  private rateSampleTimer  = 0;
  private displayedRate        = 0;
  private displayedShieldRate  = 0;
  private displayedArmorRate   = 0;

  // ── Round-end overlay ─────────────────────────────────────────────────────
  private overlayDim!:         Phaser.GameObjects.Rectangle;
  private overlayPanel!:       Phaser.GameObjects.Rectangle;
  private overlayTitle!:       Phaser.GameObjects.Text;
  private overlayHdrTexts:     Phaser.GameObjects.Text[] = [];
  private overlayRowRanks:     Phaser.GameObjects.Text[] = [];
  private overlayRowNames:     Phaser.GameObjects.Text[] = [];
  private overlayRowKills:     Phaser.GameObjects.Text[] = [];
  private overlayRowDeaths:    Phaser.GameObjects.Text[] = [];
  private overlayRowDmg:       Phaser.GameObjects.Text[] = [];
  private overlayRowStr:       Phaser.GameObjects.Text[] = [];
  private overlayBonusLabel:   Phaser.GameObjects.Text[] = [];
  private overlayBonusName:    Phaser.GameObjects.Text[] = [];
  private overlayBonusVal:     Phaser.GameObjects.Text[] = [];
  private overlayCountdown!:   Phaser.GameObjects.Text;
  private overlayBorderLines:  Phaser.GameObjects.Rectangle[] = [];
  private overlayVisible    = false;
  private prevRoundPhase    = '';

  private respawnText!:  Phaser.GameObjects.Text;
  private deathTime     = -1;
  private wasDeadLast   = false;

  // Version display (bottom-right)
  private versionText!: Phaser.GameObjects.Text;

  // Kill feed — scrolling notifications (top-left, over game viewport)
  private killFeedTexts:     Phaser.GameObjects.Text[] = [];
  private killFeedSubscribed = false;
  private static readonly KILL_FEED_MAX    = 5;
  private static readonly KILL_FEED_LIFE   = 5000; // ms before fade-out

  // Enemy overlay toggle
  private overlayToggleBg!:   Phaser.GameObjects.Rectangle;
  private overlayToggleText!: Phaser.GameObjects.Text;

  constructor() {
    super({ key: 'UIScene' });
  }

  create() {
    const screenW = this.scale.width  || window.innerWidth;
    const screenH = this.scale.height || window.innerHeight;

    // Recompute layout for this screen — touch devices with < 1024 px get a
    // narrower 300 px HUD; everything else keeps the full 420 px desktop layout.
    const hudW = getHudWidth(screenW);
    this.compactHud = hudW < 380;
    recomputeHudLayout(hudW, this.compactHud);
    this.barW     = BAR_W;
    this.mapScale = MAP_SIZE / WORLD_SIZE;

    this.hudX = screenW - HUD_W;
    this.mapX = this.hudX + HUD_PAD;
    this.mapY = HUD_PAD + MAP_LABEL_H;

    this.buildPanel(screenH);
    this.buildMinimap();
    this.buildOverlayToggle();
    this.buildStats();
    this.buildAmmo();
    this.buildScoreboard();
    this.buildTimerBox();
    this.buildOverlay(screenW, screenH);
    this.buildVersionDisplay(screenW, screenH);
    this.buildKillFeed();

    this.respawnText = this.add.text(0, 0, `RESPAWN IN: ${RESPAWN_DELAY_SECONDS}`, {
      fontSize:   '22px',
      fontFamily: 'KenvectorFuture',
      color:      '#ffcc55',
    }).setOrigin(0.5).setDepth(200).setVisible(false);

    // Restart this scene when the viewport changes (e.g. device rotation) so
    // all HUD elements are rebuilt with the correct dimensions.
    let resizeTimer: ReturnType<typeof setTimeout> | null = null;
    this.scale.on('resize', () => {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => { this.scene.restart(); }, 200);
    });
  }

  // ── Kill feed ─────────────────────────────────────────────────────────────

  private buildKillFeed() {
    const cfg = { fontSize: '11px', fontFamily: 'KenvectorFutureThin', color: '#e8c87a',
      stroke: '#000000', strokeThickness: 3 };
    for (let i = 0; i < UIScene.KILL_FEED_MAX; i++) {
      this.killFeedTexts.push(
        this.add.text(192, 12 + i * 18, '', cfg).setOrigin(0, 0).setDepth(150).setAlpha(0),
      );
    }
  }

  private pushKillFeed(line: string) {
    // Shift existing entries up by one slot
    for (let i = UIScene.KILL_FEED_MAX - 1; i > 0; i--) {
      const above = this.killFeedTexts[i - 1];
      const cur   = this.killFeedTexts[i];
      cur.setText(above.text).setAlpha(above.alpha);
      this.tweens.killTweensOf(cur);
    }
    const entry = this.killFeedTexts[0];
    entry.setText(line).setAlpha(1);
    this.tweens.killTweensOf(entry);
    this.tweens.add({
      targets:  entry,
      alpha:    0,
      delay:    UIScene.KILL_FEED_LIFE,
      duration: 800,
    });
  }

  private trySubscribeKillFeed() {
    if (this.killFeedSubscribed) return;
    const room = gameClient.room;
    if (!room) return;
    this.killFeedSubscribed = true;
    room.onMessage<PlayerKilledPayload>(ServerEvents.PLAYER_KILLED, (payload) => {
      const killerPlayer = room.state?.players?.get(payload.killerSessionId);
      const victimPlayer = room.state?.players?.get(payload.victimSessionId);
      const killerName   = killerPlayer?.displayName ?? payload.killerSessionId.slice(0, 6);
      const victimName   = victimPlayer?.displayName ?? payload.victimSessionId.slice(0, 6);
      const killerRank   = RANK_NAMES[payload.killerRankIndex] ?? 'Cadet';
      const victimRank   = RANK_NAMES[payload.victimRankIndex] ?? 'Cadet';
      this.pushKillFeed(`${killerRank} ${killerName} × ${victimRank} ${victimName}`);
    });
  }

  // ── Full-height panel background ──────────────────────────────────────────

  private buildPanel(screenH: number) {
    // Graphite instrument case with an aged alloy spine and rubber grip marks.
    addRect(this, this.hudX, 0, HUD_W, screenH, 0x0b0c0d, 0.97);
    addRect(this, this.hudX, 0, 8, screenH, 0x77756d, 1);
    addRect(this, this.hudX + 2, 0, 2, screenH, 0xc0b9a4, 0.55);
    addRect(this, this.hudX + 8, 0, 5, screenH, 0x171411, 1);
    addLine(this, this.hudX + 13, 0, 1, screenH, ACCENT_CLR, 0.72);
    for (let y = 18; y < screenH; y += 34) {
      addRect(this, this.hudX + 8, y, 5, 14, 0x020202, 0.9, 101);
    }
  }

  // ── Radar minimap ─────────────────────────────────────────────────────────

  private buildMinimap() {
    const hx = this.hudX;
    const mx = this.mapX;
    const my = this.mapY;

    // "RADAR" section label
    this.add.text(hx + HUD_W / 2, HUD_PAD - 2, 'RADAR', {
      fontSize: '14px',
      fontFamily: 'KenvectorFuture',
      color: '#e7a24d',
    })
    .setOrigin(0.5, 0)
    .setDepth(101);

    // Divider below label
    addLine(this, hx, HUD_PAD + MAP_LABEL_H - 4, HUD_W, 1, DIV_COLOR, DIV_ALPHA);

    // Map background — deep green tint for radar feel
    addRect(this, mx, my, MAP_SIZE, MAP_SIZE, 0x07100a, 0.98, 100);

    // Crosshair guide lines through map centre
    const half = Math.floor(MAP_SIZE / 2);
    addLine(this, mx,        my + half, MAP_SIZE, 1, 0x21452b, 0.8, 101);
    addLine(this, mx + half, my,        1, MAP_SIZE, 0x21452b, 0.8, 101);

    // Map border
    addLine(this, mx,            my,            MAP_SIZE, 1, ACCENT_CLR, 0.55, 102);
    addLine(this, mx,            my + MAP_SIZE, MAP_SIZE, 1, ACCENT_CLR, 0.55, 102);
    addLine(this, mx,            my,            1, MAP_SIZE, ACCENT_CLR, 0.55, 102);
    addLine(this, mx + MAP_SIZE, my,            1, MAP_SIZE, ACCENT_CLR, 0.55, 102);

    // Divider below minimap section
    addLine(this, hx, my + MAP_SIZE + HUD_PAD, HUD_W, 1, DIV_COLOR, DIV_ALPHA);

    // Pre-allocate icon pool — real ship sprites, scaled tiny, rotated each frame.
    // 'sheet' atlas is loaded by GameScene and shared across all scenes.
    const fighter = getShipVisual('fighter');
    for (let i = 0; i < MAX_DOTS; i++) {
      const pip = this.add.rectangle(0, 0, 5, 5, 0x759aa8, 0.85)
        .setOrigin(0.5)
        .setRotation(Math.PI / 4)
        .setDepth(102)
        .setVisible(false) as Phaser.GameObjects.Rectangle;
      const icon = this.add.image(0, 0, fighter.textureKey)
        .setScale(fighter.minimapScale)
        .setDepth(103)
        .setVisible(false);
      this.mapPips.push(pip);
      this.mapIcons.push(icon);
    }

    // Explosion flash pool — rectangles tweened to expand + fade on player death
    for (let i = 0; i < 6; i++) {
      const r = this.add.rectangle(0, 0, 10, 10, 0xff6600, 1)
        .setOrigin(0.5, 0.5)
        .setDepth(104)
        .setVisible(false) as Phaser.GameObjects.Rectangle;
      this.explosionPool.push(r);
    }

    // Station marker pool — up to 4 stations (currently always 1 per round)
    for (let i = 0; i < 4; i++) {
      const dot = this.add.rectangle(0, 0, 10, 10, 0x00ccff, 0.90)
        .setOrigin(0.5, 0.5)
        .setDepth(102)
        .setVisible(false) as Phaser.GameObjects.Rectangle;
      this.stationDots.push(dot);
    }

    // Pad marker pool — 2 pads per station × 4 stations
    for (let i = 0; i < 8; i++) {
      const dot = this.add.rectangle(0, 0, 6, 6, 0xff6622, 0.85)
        .setOrigin(0.5, 0.5)
        .setDepth(102)
        .setVisible(false) as Phaser.GameObjects.Rectangle;
      this.padDots.push(dot);
    }

    // Powerup dot pool — diamond-shaped (rotated square), colored by tier
    for (let i = 0; i < 24; i++) {
      const dot = this.add.rectangle(0, 0, 5, 5, 0xffffff, 0.95)
        .setOrigin(0.5, 0.5)
        .setRotation(Math.PI / 4)
        .setDepth(103)
        .setVisible(false) as Phaser.GameObjects.Rectangle;
      this.powerupDots.push(dot);
    }
  }

  // ── Enemy overlay toggle button ───────────────────────────────────────────

  private buildOverlayToggle() {
    const hx = this.hudX;
    const ty = MAP_SECTION_H;

    this.overlayToggleBg = this.add.rectangle(hx, ty, HUD_W, TOGGLE_H, 0x17130f, 1.0)
      .setOrigin(0, 0)
      .setDepth(100)
      .setInteractive({ useHandCursor: true }) as Phaser.GameObjects.Rectangle;

    this.overlayToggleText = this.add.text(hx + HUD_W / 2, ty + TOGGLE_H / 2, 'ENEMY TAGS  ON', {
      fontSize: '13px', fontFamily: 'KenvectorFutureThin', color: '#8bd66d',
    }).setOrigin(0.5, 0.5).setDepth(101);

    // Bottom divider
    addLine(this, hx, ty + TOGGLE_H, HUD_W, 1, DIV_COLOR, DIV_ALPHA);

    this.overlayToggleBg.on('pointerover',  () => this.overlayToggleBg.setFillStyle(0x2b2116, 1.0));
    this.overlayToggleBg.on('pointerout',   () => this.overlayToggleBg.setFillStyle(0x17130f, 1.0));
    this.overlayToggleBg.on('pointerdown',  () => {
      uiState.showEnemyOverlay = !uiState.showEnemyOverlay;
      const on = uiState.showEnemyOverlay;
      this.overlayToggleText.setText(on ? 'ENEMY TAGS  ON' : 'ENEMY TAGS  OFF');
      this.overlayToggleText.setColor(on ? '#8bd66d' : '#5b554d');
    });
  }

  // ── Stats bars (energy / shields / armor) ────────────────────────────────

  private buildStats() {
    const hx = this.hudX;
    const bx = hx + HUD_PAD;

    const LABELS = ['ENERGY', 'SHIELDS', 'ARMOR'];
    const COLORS = ['#f2a23a', '#62cbd5', '#87c86b'];

    for (let i = 0; i < 3; i++) {
      addRect(this, bx, rowBarY(i), BAR_W, BAR_H, 0x14120f, 0.96, 100);

      const label = this.add
        .text(bx, rowLabelY(i), LABELS[i], {
          fontSize: '18px',
          fontFamily: 'KenvectorFutureThin',
          color: COLORS[i],
        })
        .setDepth(101);

      if (i === 0) this.energyLabel = label;
      if (i === 1) this.shieldLabel = label;
      if (i === 2) this.armorLabel  = label;
    }

    // Rate texts — right-aligned on each label row
    this.energyRateText = this.add
      .text(bx + BAR_W, rowLabelY(0), '', {
        fontSize: '16px', fontFamily: 'KenvectorFutureThin', color: '#55dd77',
      })
      .setOrigin(1, 0).setDepth(101);

    this.shieldRateText = this.add
      .text(bx + BAR_W, rowLabelY(1), '', {
        fontSize: '16px', fontFamily: 'KenvectorFutureThin', color: '#55dd77',
      })
      .setOrigin(1, 0).setDepth(101);

    this.armorRateText = this.add
      .text(bx + BAR_W, rowLabelY(2), '', {
        fontSize: '16px', fontFamily: 'KenvectorFutureThin', color: '#55dd77',
      })
      .setOrigin(1, 0).setDepth(101);

    // Buff bonus texts — sit to the left of the rate text, shown in tier colour
    this.energyBuffText = this.add
      .text(bx + BAR_W - 52, rowLabelY(0), '', {
        fontSize: '14px', fontFamily: 'KenvectorFutureThin', color: '#ffffff',
      })
      .setOrigin(1, 0).setDepth(101);

    this.shieldBuffText = this.add
      .text(bx + BAR_W - 52, rowLabelY(1), '', {
        fontSize: '14px', fontFamily: 'KenvectorFutureThin', color: '#ffffff',
      })
      .setOrigin(1, 0).setDepth(101);

    this.starBuffText = this.add
      .text(bx + BAR_W - 52, rowLabelY(2), '', {
        fontSize: '14px', fontFamily: 'KenvectorFutureThin', color: '#ffffff',
      })
      .setOrigin(1, 0).setDepth(101);

    // Energy fill
    this.energyFill = addRect(this, bx, rowBarY(0), 1, BAR_H, 0xd87422, 0.92, 101);
    this.energyValueText = this.add
      .text(bx + BAR_W - 3, rowBarY(0) + BAR_H / 2, '', {
        fontSize: '16px',
        fontFamily: 'KenvectorFutureThin',
        color: '#fff0d0',
      })
      .setOrigin(1, 0.5)
      .setDepth(102);

    // Shield fill
    this.shieldFill = addRect(this, bx, rowBarY(1), 1, BAR_H, 0x278f9d, 0.92, 101);
    this.shieldValueText = this.add
      .text(bx + BAR_W - 3, rowBarY(1) + BAR_H / 2, '', {
        fontSize: '16px',
        fontFamily: 'KenvectorFutureThin',
        color: '#ccf4ff',
      })
      .setOrigin(1, 0.5)
      .setDepth(102);

    // Armor fill
    this.armorFill = addRect(this, bx, rowBarY(2), 1, BAR_H, 0x4d9a57, 0.92, 101);
    this.armorValueText = this.add
      .text(bx + BAR_W - 3, rowBarY(2) + BAR_H / 2, '', {
        fontSize: '16px',
        fontFamily: 'KenvectorFutureThin',
        color: '#ccffdd',
      })
      .setOrigin(1, 0.5)
      .setDepth(102);

    // Divider below stats section
    addLine(this, hx, ammoY(), HUD_W, 1, DIV_COLOR, DIV_ALPHA);
  }

  // ── Secondary ammo ────────────────────────────────────────────────────────

  private buildAmmo() {
    const hx = this.hudX;
    const bx = hx + HUD_PAD;

    const ay = ammoY();
    addLine(this, hx, ay + AMMO_H, HUD_W, 1, DIV_COLOR, DIV_ALPHA);

    this.ammoLabel = this.add
      .text(bx, ay + 10, 'TORPS', {
        fontSize: '14px',
        fontFamily: 'KenvectorFutureThin',
        color: '#e79a3b',
      })
      .setDepth(101);

    this.ammoText = this.add
      .text(bx + BAR_W, ay + 10, '0 / 0', {
        fontSize: '14px',
        fontFamily: 'KenvectorFutureThin',
        color: '#f4d6a4',
      })
      .setOrigin(1, 0)
      .setDepth(101);

    this.ammoPips = this.add
      .text(bx + BAR_W / 2, ay + 10, '', {
        fontSize: '13px',
        fontFamily: 'KenvectorFutureThin',
        color: '#e79a3b',
      })
      .setOrigin(0.5, 0)
      .setDepth(101);
  }

  // ── Pilots scoreboard (bottom of right panel) ────────────────────────────

  private buildScoreboard() {
    const hx  = this.hudX;
    const bx  = hx + HUD_PAD;
    const HDR = '#8b6843';
    const hy  = pilotsY();

    // Section divider + "PILOTS" label
    addLine(this, hx, hy, HUD_W, 1, DIV_COLOR, DIV_ALPHA);
    this.add.text(hx + HUD_W / 2, hy + 6, 'PILOTS', {
      fontSize: '14px', fontFamily: 'KenvectorFuture', color: '#e7a24d',
    }).setOrigin(0.5, 0).setDepth(101);

    // Column headers
    const hdrY  = hy + PILOTS_LABEL_H;
    const hdrCfg = { fontSize: '10px', fontFamily: 'KenvectorFuture', color: HDR };
    this.add.text(bx + COL_RANK,   hdrY, '#',      hdrCfg).setOrigin(1, 0).setDepth(101);
    this.add.text(bx + COL_NAME,   hdrY, 'PILOT',  hdrCfg).setOrigin(0, 0).setDepth(101);
    if (!this.compactHud) {
      this.add.text(bx + COL_SHIP,   hdrY, 'RANK',   hdrCfg).setOrigin(0, 0).setDepth(101);
    }
    this.add.text(bx + COL_KILLS,  hdrY, 'KILLS',  hdrCfg).setOrigin(1, 0).setDepth(101);
    this.add.text(bx + COL_DEATHS, hdrY, 'DEATHS', hdrCfg).setOrigin(1, 0).setDepth(101);

    // Thin rule under headers
    addLine(this, bx, hdrY + 13, BAR_W, 1, DIV_COLOR, 0.5);

    // Pre-allocate row cells
    const rowCfg = { fontSize: '11px', fontFamily: 'KenvectorFuture', color: '#d7d2c6' };
    for (let i = 0; i < MAX_SCORE_ROWS; i++) {
      const y = hy + PILOTS_LABEL_H + 17 + i * PILOTS_ROW_H;
      this.scoreRanks .push(this.add.text(bx + COL_RANK,   y, '', rowCfg).setOrigin(1, 0).setDepth(101).setVisible(false));
      this.scoreNames .push(this.add.text(bx + COL_NAME,   y, '', rowCfg).setOrigin(0, 0).setDepth(101).setVisible(false));
      // SHIP column: park off-screen in compact mode; never shown
      this.scoreShips .push(this.compactHud
        ? this.add.text(-9999, -9999, '', rowCfg).setVisible(false)
        : this.add.text(bx + COL_SHIP, y, '', rowCfg).setOrigin(0, 0).setDepth(101).setVisible(false));
      this.scoreKills .push(this.add.text(bx + COL_KILLS,  y, '', rowCfg).setOrigin(1, 0).setDepth(101).setVisible(false));
      this.scoreDeaths.push(this.add.text(bx + COL_DEATHS, y, '', rowCfg).setOrigin(1, 0).setDepth(101).setVisible(false));
    }
  }

  // ── Round timer box (top-left) ────────────────────────────────────────────

  private buildTimerBox() {
    const x = 12, y = 12, w = 168, h = 84;
    const C = 14;

    addRect(this, x, y, w, h, 0x11100f, 0.94);
    addLine(this, x,     y,         w,     1, DIV_COLOR,  DIV_ALPHA);
    addLine(this, x,     y + h,     w + 1, 1, DIV_COLOR,  DIV_ALPHA);
    addLine(this, x,     y,         1,     h, DIV_COLOR,  DIV_ALPHA);
    addLine(this, x + w, y,         1, h + 1, DIV_COLOR,  DIV_ALPHA);
    // corner accents
    addLine(this, x,         y,         C, 1, ACCENT_CLR, 0.9);
    addLine(this, x,         y,         1, C, ACCENT_CLR, 0.9);
    addLine(this, x + w - C, y,         C, 1, ACCENT_CLR, 0.9);
    addLine(this, x + w,     y,         1, C, ACCENT_CLR, 0.9);
    addLine(this, x,         y + h - 1, C, 1, ACCENT_CLR, 0.9);
    addLine(this, x,         y + h - C, 1, C, ACCENT_CLR, 0.9);
    addLine(this, x + w - C, y + h - 1, C, 1, ACCENT_CLR, 0.9);
    addLine(this, x + w,     y + h - C, 1, C, ACCENT_CLR, 0.9);

    this.timerRoundLabel = this.add.text(x + w / 2, y + 8, 'ROUND 1', {
      fontSize: '11px', fontFamily: 'KenvectorFuture', color: '#b77b3b',
    }).setOrigin(0.5, 0).setDepth(101);

    this.roundTimerText = this.add.text(x + w / 2, y + 26, '--:--', {
      fontSize: '26px', fontFamily: 'KenvectorFuture', color: '#f4e6c9',
    }).setOrigin(0.5, 0).setDepth(101);

    this.arenaNameText = this.add.text(x + w / 2, y + 66, 'THE VOID', {
      fontSize: '9px', fontFamily: 'KenvectorFuture', color: '#71614d',
    }).setOrigin(0.5, 0).setDepth(101);
  }

  // ── Version display (bottom-right) ───────────────────────────────────────

  private buildVersionDisplay(screenW: number, screenH: number) {
    const feVer = __CLIENT_VERSION__;
    this.versionText = this.add.text(
      screenW - HUD_PAD,
      screenH - 10,
      `FE ${feVer}  BE …`,
      { fontSize: '9px', fontFamily: 'KenvectorFutureThin', color: '#5e4c37' },
    ).setOrigin(1, 1).setDepth(102);

    fetch('/api/version')
      .then((r) => r.json() as Promise<{ version: string }>)
      .then(({ version }) => {
        this.versionText.setText(`FE ${feVer}  BE ${version}`);
      })
      .catch(() => {
        this.versionText.setText(`FE ${feVer}  BE ?`);
      });
  }

  // ── Round-end overlay ─────────────────────────────────────────────────────

  private buildOverlay(sw: number, sh: number) {
    const px = Math.round((sw - OV_W) / 2);
    const py = Math.round((sh - OV_H) / 2);

    this.overlayDim = addRect(this, 0, 0, sw, sh, 0x000000, 0.72, 200)
      .setVisible(false) as Phaser.GameObjects.Rectangle;

    this.overlayPanel = addRect(this, px, py, OV_W, OV_H, 0x050d1e, 0.97, 201)
      .setVisible(false) as Phaser.GameObjects.Rectangle;

    const ac  = 0x33aadd;
    const bdr = 0x1a4a70;
    const C   = OV_CORNER;
    this.overlayBorderLines = [
      addLine(this, px,          py,           OV_W, 1,    bdr, 0.85, 201),
      addLine(this, px,          py + OV_H-1,  OV_W, 1,    bdr, 0.85, 201),
      addLine(this, px,          py,           1,    OV_H, bdr, 0.85, 201),
      addLine(this, px + OV_W-1, py,           1,    OV_H, bdr, 0.85, 201),
      // TL
      addLine(this, px,          py,           C, 1, ac, 1, 202),
      addLine(this, px,          py,           1, C, ac, 1, 202),
      // TR
      addLine(this, px + OV_W-C, py,           C, 1, ac, 1, 202),
      addLine(this, px + OV_W-1, py,           1, C, ac, 1, 202),
      // BL
      addLine(this, px,          py + OV_H-1,  C, 1, ac, 1, 202),
      addLine(this, px,          py + OV_H-C,  1, C, ac, 1, 202),
      // BR
      addLine(this, px + OV_W-C, py + OV_H-1, C, 1, ac, 1, 202),
      addLine(this, px + OV_W-1, py + OV_H-C, 1, C, ac, 1, 202),
    ];
    this.overlayBorderLines.forEach((l) => l.setVisible(false));

    this.overlayTitle = this.add.text(sw / 2, py + 22, '', {
      fontSize: '20px',
      fontFamily: 'KenvectorFuture',
      color: '#c8ddff',
    }).setOrigin(0.5, 0).setDepth(202).setScrollFactor(0).setVisible(false);

    // Column headers — each at its own x position
    const colY   = py + OV_HEADER_H;
    const hdrCfg = { fontSize: '11px', fontFamily: 'KenvectorFuture', color: '#4488bb' };
    const addHdr = (x: number, label: string, originX: number) => {
      const t = this.add.text(px + x, colY, label, hdrCfg)
        .setOrigin(originX, 0).setDepth(202).setScrollFactor(0).setVisible(false);
      this.overlayHdrTexts.push(t);
    };
    addHdr(OV_COL_RANK, '#',      1);
    addHdr(OV_COL_NAME, 'PILOT',  0);
    addHdr(OV_COL_K,    'KILLS',  1);
    addHdr(OV_COL_D,    'DEATHS', 1);
    addHdr(OV_COL_DMG,  'DAMAGE', 1);
    addHdr(OV_COL_STR,  'STREAK', 1);

    const divider = addLine(this, px + 12, colY + 18, OV_W - 24, 1, 0x1a3a60, 0.8, 202);
    divider.setVisible(false);
    this.overlayBorderLines.push(divider);

    // Pre-allocate one Text per column per row
    const rowCfg = { fontSize: '12px', fontFamily: 'KenvectorFuture', color: '#aaccff' };
    for (let i = 0; i < OV_MAX_ROWS; i++) {
      const y = colY + 24 + i * OV_ROW_H;
      const cell = (x: number, originX: number) =>
        this.add.text(px + x, y, '', rowCfg)
          .setOrigin(originX, 0).setDepth(202).setScrollFactor(0).setVisible(false);
      this.overlayRowRanks .push(cell(OV_COL_RANK, 1));
      this.overlayRowNames .push(cell(OV_COL_NAME, 0));
      this.overlayRowKills .push(cell(OV_COL_K,    1));
      this.overlayRowDeaths.push(cell(OV_COL_D,    1));
      this.overlayRowDmg   .push(cell(OV_COL_DMG,  1));
      this.overlayRowStr   .push(cell(OV_COL_STR,  1));
    }

    const bonusDivY = colY + 24 + OV_MAX_ROWS * OV_ROW_H + 4;
    const bonusDiv  = addLine(this, px + 12, bonusDivY, OV_W - 24, 1, 0x1a3a60, 0.8, 202);
    bonusDiv.setVisible(false);
    this.overlayBorderLines.push(bonusDiv);

    // Bonus section — 3 rows × 3 columns (label | name | value)
    const bonusLabelCfg = { fontSize: '12px', fontFamily: 'KenvectorFuture', color: '#55ccaa' };
    const bonusValCfg   = { fontSize: '12px', fontFamily: 'KenvectorFuture', color: '#aaccff' };
    const BONUS_LABELS  = ['MOST DAMAGE', 'MOST DAMAGED', 'LONGEST STREAK'];
    for (let i = 0; i < 3; i++) {
      const y = bonusDivY + 12 + i * 24;
      this.overlayBonusLabel.push(
        this.add.text(px + OV_BONUS_LABEL_X, y, BONUS_LABELS[i], bonusLabelCfg)
          .setOrigin(0, 0).setDepth(202).setScrollFactor(0).setVisible(false),
      );
      this.overlayBonusName.push(
        this.add.text(px + OV_BONUS_NAME_X, y, '', bonusLabelCfg)
          .setOrigin(0, 0).setDepth(202).setScrollFactor(0).setVisible(false),
      );
      this.overlayBonusVal.push(
        this.add.text(px + OV_COL_STR, y, '', bonusValCfg)
          .setOrigin(1, 0).setDepth(202).setScrollFactor(0).setVisible(false),
      );
    }

    this.overlayCountdown = this.add.text(sw / 2, py + OV_H - 28, '', {
      fontSize: '14px',
      fontFamily: 'KenvectorFuture',
      color: '#4488ff',
    }).setOrigin(0.5, 0).setDepth(202).setScrollFactor(0).setVisible(false);
  }

  private showOverlay(roundNumber: number) {
    this.overlayVisible = true;
    this.overlayDim.setVisible(true);
    this.overlayPanel.setVisible(true);
    this.overlayTitle.setText(`ROUND ${roundNumber} COMPLETE`).setVisible(true);
    this.overlayHdrTexts.forEach((t) => t.setVisible(true));
    this.overlayBonusLabel.forEach((t) => t.setVisible(true));
    this.overlayBonusName.forEach((t) => t.setVisible(true));
    this.overlayBonusVal.forEach((t) => t.setVisible(true));
    this.overlayCountdown.setVisible(true);
    this.overlayBorderLines.forEach((l) => l.setVisible(true));
    this.refreshOverlayContent();
  }

  private hideOverlay() {
    this.overlayVisible = false;
    this.overlayDim.setVisible(false);
    this.overlayPanel.setVisible(false);
    this.overlayTitle.setVisible(false);
    this.overlayHdrTexts.forEach((t) => t.setVisible(false));
    for (let i = 0; i < OV_MAX_ROWS; i++) {
      this.overlayRowRanks [i].setVisible(false);
      this.overlayRowNames [i].setVisible(false);
      this.overlayRowKills [i].setVisible(false);
      this.overlayRowDeaths[i].setVisible(false);
      this.overlayRowDmg   [i].setVisible(false);
      this.overlayRowStr   [i].setVisible(false);
    }
    this.overlayBonusLabel.forEach((t) => t.setVisible(false));
    this.overlayBonusName.forEach((t) => t.setVisible(false));
    this.overlayBonusVal.forEach((t) => t.setVisible(false));
    this.overlayCountdown.setVisible(false);
    this.overlayBorderLines.forEach((l) => l.setVisible(false));
  }

  private refreshOverlayContent() {
    const room = gameClient.room;
    if (!room?.state?.players) return;

    const players: PlayerState[] = [];
    room.state.players.forEach((p: PlayerState) => players.push(p));
    players.sort((a, b) =>
      (b.roundKills - b.roundDeaths) - (a.roundKills - a.roundDeaths) || b.roundKills - a.roundKills
    );

    for (let i = 0; i < OV_MAX_ROWS; i++) {
      if (i >= players.length) {
        this.overlayRowRanks [i].setVisible(false);
        this.overlayRowNames [i].setVisible(false);
        this.overlayRowKills [i].setVisible(false);
        this.overlayRowDeaths[i].setVisible(false);
        this.overlayRowDmg   [i].setVisible(false);
        this.overlayRowStr   [i].setVisible(false);
        continue;
      }
      const p     = players[i];
      const color = i < 3 ? MEDAL_COLORS[i] : '#aaccff';
      this.overlayRowRanks [i].setText(i < 3 ? String(i + 1) : '').setColor(color).setVisible(true);
      this.overlayRowNames [i].setText(p.displayName.slice(0, 22)).setColor(color).setVisible(true);
      this.overlayRowKills [i].setText(String(p.roundKills)).setColor(color).setVisible(true);
      this.overlayRowDeaths[i].setText(String(p.roundDeaths)).setColor(color).setVisible(true);
      this.overlayRowDmg   [i].setText(String(Math.round(p.damageDealt))).setColor(color).setVisible(true);
      this.overlayRowStr   [i].setText(formatTime(p.longestStreak)).setColor(color).setVisible(true);
    }

    const byDmgDealt = [...players].sort((a, b) => b.damageDealt - a.damageDealt);
    const byDmgRecv  = [...players].sort((a, b) => b.damageReceived - a.damageReceived);
    const byStreak   = [...players].sort((a, b) => b.longestStreak - a.longestStreak);

    this.overlayBonusName[0].setText(byDmgDealt[0]?.displayName.slice(0, 20) ?? '--');
    this.overlayBonusName[1].setText(byDmgRecv [0]?.displayName.slice(0, 20) ?? '--');
    this.overlayBonusName[2].setText(byStreak  [0]?.displayName.slice(0, 20) ?? '--');

    this.overlayBonusVal[0].setText(String(Math.round(byDmgDealt[0]?.damageDealt   ?? 0)));
    this.overlayBonusVal[1].setText(String(Math.round(byDmgRecv [0]?.damageReceived ?? 0)));
    this.overlayBonusVal[2].setText(formatTime(byStreak[0]?.longestStreak ?? 0));
  }

  // ── Update ────────────────────────────────────────────────────────────────

  override update(_time: number, delta: number) {
    const room = gameClient.room;

    this.trySubscribeExplosions(room);
    this.trySubscribePowerupEvents(room);
    this.trySubscribeKillFeed();

    if (!room?.state?.players) {
      this.clearBars();
      this.clearDots();
      return;
    }

    if (!this.obstaclesDrawn) {
      this.obstaclesDrawn = true;
      this.drawObstaclesOnMap(room);
    }

    // ── Round phase transitions ───────────────────────────────────────────────
    const roundPhase = room.state.roundPhase ?? 'playing';
    if (roundPhase !== this.prevRoundPhase) {
      if (roundPhase === 'roundEnd') {
        this.showOverlay(room.state.roundNumber ?? 1);
      } else if (this.overlayVisible) {
        this.hideOverlay();
      }
      if (roundPhase === 'playing' && this.prevRoundPhase !== '') {
        this.sound.play('sfx_zap', { volume: 0.4 });
      }
      this.prevRoundPhase = roundPhase;
    }
    if (this.overlayVisible && roundPhase === 'roundEnd') {
      this.overlayCountdown.setText(`NEXT ROUND IN:  ${Math.ceil(room.state.roundTimeRemaining ?? 0)}`);
    }

    // ── Round timer box ───────────────────────────────────────────────────────
    const rt = room.state.roundTimeRemaining ?? 0;
    const rn = room.state.roundNumber ?? 1;
    this.timerRoundLabel.setText(`ROUND ${rn}`);
    if (roundPhase === 'roundEnd') {
      this.roundTimerText.setText('END').setColor('#ff7744');
    } else if (roundPhase === 'waiting') {
      this.roundTimerText.setText('--:--').setColor('#9b7041');
    } else {
      this.roundTimerText.setText(formatTime(rt)).setColor('#f4e6c9');
    }

    // ── Pilots scoreboard — sorted by K–D net score, kills as tiebreaker ─────
    const players: PlayerState[] = [];
    room.state.players.forEach((p: PlayerState) => players.push(p));
    players.sort((a, b) =>
      (b.kills - b.deaths) - (a.kills - a.deaths) || b.kills - a.kills
    );

    players.forEach((p, i) => {
      if (i >= MAX_SCORE_ROWS) return;
      const isLocal = p.sessionId === room.sessionId;
      const color   = isLocal ? '#fff0d0' : '#918b7d';
      const shipDef = SHIP_DEFINITIONS[p.shipId as ShipId];
      const rankName = RANK_NAMES[p.rankIndex ?? 0] ?? 'Cadet';
      this.scoreRanks [i].setText(String(i + 1)).setColor(color).setVisible(true);
      this.scoreNames [i].setText(p.displayName.slice(0, 18)).setColor(color).setVisible(true);
      this.scoreShips [i].setText(this.compactHud ? '' : rankName).setColor(color).setVisible(!this.compactHud);
      this.scoreKills [i].setText(String(p.kills)).setColor(color).setVisible(true);
      this.scoreDeaths[i].setText(String(p.deaths)).setColor(color).setVisible(true);
    });
    for (let i = players.length; i < MAX_SCORE_ROWS; i++) {
      this.scoreRanks [i].setVisible(false);
      this.scoreNames [i].setVisible(false);
      this.scoreShips [i].setVisible(false);
      this.scoreKills [i].setVisible(false);
      this.scoreDeaths[i].setVisible(false);
    }

    // Minimap
    this.updateMinimap(players, room.sessionId);

    // Local player
    const localPlayer = room.state.players.get(room.sessionId);

    const nowDead = !localPlayer || localPlayer.isDead;
    if (nowDead && !this.wasDeadLast) this.deathTime = Date.now();
    this.wasDeadLast = nowDead;

    if (nowDead) {
      const elapsed  = (Date.now() - this.deathTime) / 1000;
      const secs     = Math.max(0, Math.ceil(RESPAWN_DELAY_SECONDS - elapsed));
      this.respawnText
        .setPosition(this.scale.width / 2, this.scale.height / 2)
        .setVisible(true)
        .setText(secs > 0 ? `RESPAWN IN: ${secs}` : 'RESPAWNING...');
      this.setBarsDead();
      return;
    }
    this.respawnText.setVisible(false);

    const def = SHIP_DEFINITIONS[localPlayer.shipId as ShipId] ?? SHIP_DEFINITIONS.fighter;

    // ── Energy ───────────────────────────────────────────────────────────────
    this.energyLabel.setAlpha(1);
    this.energyValueText.setAlpha(1);

    const curE   = Math.max(0, localPlayer.energy);
    const maxE   = def.energy;
    const pctE   = Math.min(1, curE / maxE);
    const fillWE = Math.max(1, Math.round(this.barW * pctE));

    this.rateSampleTimer += delta;
    if (this.rateSampleTimer >= 500) {
      const dt = this.rateSampleTimer / 1000;
      if (this.rateEnergySample >= 0) {
        this.displayedRate       = (curE - this.rateEnergySample) / dt;
        this.displayedShieldRate = (Math.max(0, localPlayer.shield) - this.rateShieldSample) / dt;
        this.displayedArmorRate  = (Math.max(0, localPlayer.armor)  - this.rateArmorSample)  / dt;
      }
      this.rateEnergySample = curE;
      this.rateShieldSample = Math.max(0, localPlayer.shield);
      this.rateArmorSample  = Math.max(0, localPlayer.armor);
      this.rateSampleTimer  = 0;
    }

    const rate    = Math.round(this.displayedRate);
    const rateStr = (rate >= 0 ? '+' : '') + rate;
    const rateCol = rate >= 0 ? '#55dd77' : (rate > -15 ? '#ffcc44' : '#ff6633');
    this.energyRateText.setText(rateStr).setColor(rateCol);

    // Bolt buff bonus
    if (this.activeBoltBuff && Date.now() < this.activeBoltBuff.expiresAt) {
      const mult  = TIER_MULTIPLIERS[this.activeBoltBuff.tier as PowerupTier] ?? 1;
      const bonus = Math.round(def.recharge * (mult - 1));
      const hex   = tierHex(this.activeBoltBuff.tier);
      this.energyBuffText.setText(`+${bonus}`).setColor(hex).setVisible(true);
    } else {
      this.activeBoltBuff = null;
      this.energyBuffText.setText('').setVisible(false);
    }

    const fillColorE = pctE > 0.5 ? 0xdc8329 : pctE > 0.25 ? 0xb75a20 : 0xd33b25;
    this.energyFill.setSize(fillWE, BAR_H);
    (this.energyFill as any).setFillStyle(fillColorE, 0.88);
    this.energyValueText.setText(`${Math.round(curE)}/${maxE}`);

    // ── Shield ───────────────────────────────────────────────────────────────
    this.shieldLabel.setAlpha(1);
    this.shieldValueText.setAlpha(1);

    const curS   = Math.max(0, localPlayer.shield);
    const maxS   = def.shield * getRankMult(localPlayer.rankIndex ?? 0);
    const pctS   = maxS > 0 ? Math.min(1, curS / maxS) : 0;
    const fillWS = Math.max(1, Math.round(this.barW * pctS));

    const fillColorS = pctS > 0.5 ? 0x00ccdd : pctS > 0.25 ? 0x0077aa : 0xff9900;
    this.shieldFill.setSize(fillWS, BAR_H);
    (this.shieldFill as any).setFillStyle(fillColorS, 0.88);
    this.shieldValueText.setText(`${Math.round(curS)}/${Math.round(maxS)}`);

    const shieldRate    = Math.round(this.displayedShieldRate);
    const shieldRateStr = shieldRate === 0 ? '' : (shieldRate > 0 ? '+' : '') + shieldRate;
    const shieldRateCol = shieldRate >= 0 ? '#55dd77' : (shieldRate > -20 ? '#ffcc44' : '#ff6633');
    this.shieldRateText.setText(shieldRateStr).setColor(shieldRateCol);

    // Shield buff bonus
    if (this.activeShieldBuff && Date.now() < this.activeShieldBuff.expiresAt) {
      const mult  = TIER_MULTIPLIERS[this.activeShieldBuff.tier as PowerupTier] ?? 1;
      const bonus = Math.round(def.shieldRecharge * (mult - 1));
      const hex   = tierHex(this.activeShieldBuff.tier);
      this.shieldBuffText.setText(`+${bonus}`).setColor(hex).setVisible(true);
    } else {
      this.activeShieldBuff = null;
      this.shieldBuffText.setText('').setVisible(false);
    }

    // ── Armor ────────────────────────────────────────────────────────────────
    this.armorLabel.setAlpha(1);
    this.armorValueText.setAlpha(1);

    const curA   = Math.max(0, localPlayer.armor);
    const maxA   = def.armor;
    const pctA   = Math.min(1, curA / maxA);
    const fillWA = Math.max(1, Math.round(this.barW * pctA));

    const fillColorA = pctA > 0.5 ? 0x33bb55 : pctA > 0.25 ? 0x88aa00 : 0xff4400;
    this.armorFill.setSize(fillWA, BAR_H);
    (this.armorFill as any).setFillStyle(fillColorA, 0.88);
    this.armorValueText.setText(`${Math.round(curA)}/${maxA}`);

    const armorRate    = Math.round(this.displayedArmorRate);
    const armorRateStr = armorRate === 0 ? '' : (armorRate > 0 ? '+' : '') + armorRate;
    const armorRateCol = armorRate >= 0 ? '#55dd77' : (armorRate > -20 ? '#ffcc44' : '#ff6633');
    this.armorRateText.setText(armorRateStr).setColor(armorRateCol);

    // Star buff — weapon damage multiplier shown on the armor row
    if (this.activeStarBuff && Date.now() < this.activeStarBuff.expiresAt) {
      const mult = TIER_MULTIPLIERS[this.activeStarBuff.tier as PowerupTier] ?? 1;
      const hex  = tierHex(this.activeStarBuff.tier);
      this.starBuffText.setText(`×${mult.toFixed(1)}`).setColor(hex).setVisible(true);
    } else {
      this.activeStarBuff = null;
      this.starBuffText.setText('').setVisible(false);
    }

    // ── Secondary ammo ───────────────────────────────────────────────────────
    const secDef  = SECONDARY_WEAPONS[localPlayer.shipId as ShipId] ?? SECONDARY_WEAPONS.fighter;
    const curAmmo = localPlayer.secondaryAmmo;
    const maxAmmo = secDef.magazine + SECONDARY_AMMO_BONUS[localPlayer.rankIndex ?? 0];

    const weaponName = secDef.type === 'missile' ? 'MISSILES' : 'TORPS';
    const ammoColor  = '#' + secDef.color.toString(16).padStart(6, '0');

    this.ammoLabel.setText(weaponName).setColor(ammoColor).setAlpha(1);
    this.ammoText.setText(`${curAmmo} / ${maxAmmo}`).setAlpha(1);

    const pips = Array.from({ length: maxAmmo }, (_, i) => i < curAmmo ? '\u25A0' : '\u25A1').join(' ');
    this.ammoPips.setText(pips).setColor(curAmmo > 0 ? ammoColor : '#555555').setAlpha(1);
  }

  // ── Explosion subscription ────────────────────────────────────────────────

  private trySubscribeExplosions(room: typeof gameClient.room) {
    if (this.explosionSubscribed || !room) return;
    this.explosionSubscribed = true;

    const unsub = room.onMessage(ServerEvents.PLAYER_DIED, (payload: PlayerDiedPayload) => {
      const player = gameClient.room?.state?.players?.get(payload.sessionId);
      if (player) this.flashMinimapExplosion(player.x, player.y);
    });
    this.events.once('shutdown', () => { unsub?.(); });
  }

  private trySubscribePowerupEvents(room: typeof gameClient.room) {
    if (this.powerupSubscribed || !room) return;
    this.powerupSubscribed = true;

    const unsub = room.onMessage<PowerupCollectedPayload>(ServerEvents.POWERUP_COLLECTED, (payload) => {
      if (payload.collectorSessionId !== room.sessionId) return;
      const expiresAt = Date.now() + BUFF_DURATION * 1000;
      if (payload.type === 'bolt')   this.activeBoltBuff   = { tier: payload.tier, expiresAt };
      if (payload.type === 'shield') this.activeShieldBuff = { tier: payload.tier, expiresAt };
      if (payload.type === 'star')   this.activeStarBuff   = { tier: payload.tier, expiresAt };
    });
    this.events.once('shutdown', () => { unsub?.(); });
  }

  private flashMinimapExplosion(worldX: number, worldY: number) {
    const mx = this.mapX + worldX * this.mapScale;
    const my = this.mapY + worldY * this.mapScale;

    const flash = this.explosionPool.find(r => !r.visible) ?? this.explosionPool[0];

    this.tweens.killTweensOf(flash);
    flash.setPosition(mx, my);
    flash.setSize(10, 10);
    flash.setScale(1);
    flash.setAlpha(1);
    (flash as any).setFillStyle(0xff6600, 1);
    flash.setVisible(true);

    this.tweens.add({
      targets:  flash,
      scaleX:   4,
      scaleY:   4,
      alpha:    0,
      duration: 700,
      ease:     'Power2Out',
      onComplete: () => flash.setVisible(false),
    });
  }

  // ── Minimap obstacle dots (drawn once on first room connection) ───────────

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private drawObstaclesOnMap(room: any) {
    const meta = room.metadata as { arenaName?: string; obstacleDensity?: number; roomName?: string } | null;
    const arenaName = meta?.arenaName ?? 'The Void';
    this.arenaNameText?.setText(arenaName.toUpperCase());
    const base = ARENAS[arenaName] ?? ARENAS['The Void'];
    const config = meta?.obstacleDensity !== undefined
      ? { ...base, obstacleDensity: meta.obstacleDensity }
      : base;

    for (const obs of generateObstacles(config)) {
      const mx   = this.mapX + obs.x * this.mapScale;
      const my   = this.mapY + obs.y * this.mapScale;
      const size = Math.max(2, Math.round(obs.radius * this.mapScale));

      const color = obs.texture === 'ext' && !obs.frame.startsWith('spaceMeteors')
        ? 0x1e5a6a   // stations / buildings — cool teal
        : 0x4a3828;  // asteroids (all sizes) — rocky brown

      this.add.rectangle(mx, my, size, size, color, 0.85)
        .setOrigin(0.5, 0.5)
        .setDepth(101);
    }
  }

  // ── Minimap icon update ───────────────────────────────────────────────────

  private updateMinimap(players: PlayerState[], localSessionId: string) {
    let idx = 0;
    for (const p of players) {
      if (p.isDead || idx >= MAX_DOTS) continue;

      const icon    = this.mapIcons[idx];
      const pip     = this.mapPips[idx++];
      const isLocal = p.sessionId === localSessionId;

      const mx = this.mapX + p.x * this.mapScale;
      const my = this.mapY + p.y * this.mapScale;

      const visual = getShipVisual(p.shipId);

      pip.setPosition(mx, my);
      pip.setDisplaySize(isLocal ? 7 : 5, isLocal ? 7 : 5);
      pip.setFillStyle(isLocal ? 0xf1a13c : 0x6f95a0, isLocal ? 1 : 0.72);
      pip.setVisible(true);

      icon.setPosition(mx, my);
      if (visual.frame === undefined) icon.setTexture(visual.textureKey);
      else icon.setTexture(visual.textureKey, visual.frame);
      const mapScale = isLocal ? visual.minimapScale * 1.25 : visual.minimapScale;
      icon.setScale(mapScale * (visual.widthScale ?? 1), mapScale);
      icon.setRotation(p.rotation);
      icon.setAlpha(isLocal ? 1.0 : 0.6);
      icon.setTint(isLocal ? 0xffffff : 0xaaccff);
      icon.setVisible(true);
    }

    for (let i = idx; i < this.mapIcons.length; i++) {
      this.mapPips[i].setVisible(false);
      this.mapIcons[i].setVisible(false);
    }

    // Station markers + pad markers
    const room = gameClient.room;
    let si = 0;
    let pi = 0;
    room?.state?.stations?.forEach((station) => {
      if (si < this.stationDots.length) {
        const dot = this.stationDots[si++];
        dot.setPosition(this.mapX + station.x * this.mapScale, this.mapY + station.y * this.mapScale);
        dot.setVisible(true);
      }
      for (const [px, py] of [[station.pad0X, station.pad0Y], [station.pad1X, station.pad1Y]]) {
        if (pi >= this.padDots.length) return;
        const pdot = this.padDots[pi++];
        pdot.setPosition(this.mapX + px * this.mapScale, this.mapY + py * this.mapScale);
        pdot.setVisible(true);
      }
    });
    for (let i = si; i < this.stationDots.length; i++) this.stationDots[i].setVisible(false);
    for (let i = pi; i < this.padDots.length; i++)     this.padDots[i].setVisible(false);

    // Powerup dots — one diamond per active pickup, coloured by tier
    let pui = 0;
    room?.state?.powerups?.forEach((powerup) => {
      if (pui >= this.powerupDots.length) return;
      const dot   = this.powerupDots[pui++];
      const color = TIER_COLORS[powerup.tier as PowerupTier] ?? 0xffffff;
      dot.setPosition(this.mapX + powerup.x * this.mapScale, this.mapY + powerup.y * this.mapScale);
      (dot as any).setFillStyle(color, 0.95);
      dot.setVisible(true);
    });
    for (let i = pui; i < this.powerupDots.length; i++) this.powerupDots[i].setVisible(false);
  }

  // ── State helpers ─────────────────────────────────────────────────────────

  private clearBars() {
    this.energyFill.setSize(1, BAR_H);
    this.energyValueText.setText('');
    this.shieldFill.setSize(1, BAR_H);
    this.shieldValueText.setText('');
    this.armorFill.setSize(1, BAR_H);
    this.armorValueText.setText('');
  }

  private clearDots() {
    for (const icon of this.mapIcons)    icon.setVisible(false);
    for (const dot  of this.powerupDots) dot.setVisible(false);
  }

  private setBarsDead() {
    this.energyFill.setSize(1, BAR_H);
    this.energyLabel.setAlpha(0.25);
    this.energyValueText.setText('--/--').setAlpha(0.25);
    this.energyRateText.setText('');
    this.energyBuffText.setText('').setVisible(false);

    this.shieldFill.setSize(1, BAR_H);
    this.shieldLabel.setAlpha(0.25);
    this.shieldValueText.setText('--/--').setAlpha(0.25);
    this.shieldRateText.setText('');
    this.shieldBuffText.setText('').setVisible(false);

    this.armorFill.setSize(1, BAR_H);
    this.armorLabel.setAlpha(0.25);
    this.armorValueText.setText('--/--').setAlpha(0.25);
    this.armorRateText.setText('');
    this.starBuffText.setText('').setVisible(false);

    this.rateEnergySample = -1;
    this.rateShieldSample = -1;
    this.rateArmorSample  = -1;
    this.rateSampleTimer  = 0;
    this.displayedRate        = 0;
    this.displayedShieldRate  = 0;
    this.displayedArmorRate   = 0;

    this.ammoLabel.setAlpha(0.25);
    this.ammoText.setText('--').setAlpha(0.25);
    this.ammoPips.setText('').setAlpha(0.25);
  }
}
